import type { RouteMonitoringVisitShipping } from '@/pages/Business/pages/Monitoring/models';
import { normalizeCommerceBlock } from './normalize-commerce-block';
import { asRecord, pickNum, pickStr } from './object-pickers';

function normalizeCommercialServiceBlock(
  raw: unknown
): RouteMonitoringVisitShipping['commercialService'] {
  const o = asRecord(raw);
  if (!o) return null;
  const id = pickStr(o, 'id', 'id');
  const name = pickStr(o, 'name', 'name');
  const commercialName = pickStr(o, 'commercialName', 'commercial_name');
  if (!id && !name && !commercialName) return null;
  return {
    id: id ?? undefined,
    name: name ?? undefined,
    commercialName: commercialName ?? undefined,
  };
}

function normalizeLogisticOperatorBlock(
  raw: unknown
): RouteMonitoringVisitShipping['logisticOperator'] {
  const o = asRecord(raw);
  if (!o) return null;
  const name = pickStr(o, 'name', 'name');
  const businessName = pickStr(o, 'businessName', 'business_name');
  if (!name && !businessName) return null;
  return {
    name: name ?? '',
    businessName: businessName ?? null,
  };
}

function normalizeServiceWithOperatorBlock(
  raw: unknown
): RouteMonitoringVisitShipping['serviceWithOperator'] | undefined {
  const o = asRecord(raw);
  if (!o) return undefined;
  const csRaw = o.commercialService ?? o.commercial_service;
  const cs = normalizeCommercialServiceBlock(csRaw);
  if (!cs) return undefined;
  return {
    commercialService: {
      id: cs.id ?? '—',
      name: cs.name ?? cs.commercialName ?? '',
      commercialName: cs.commercialName ?? cs.name ?? '',
    },
  };
}

export function coalesceShippingsRaw(
  visit: Record<string, unknown>
): unknown[] {
  const arr = visit.shippings;
  if (Array.isArray(arr) && arr.length > 0) return arr;
  const single = visit.shipping;
  if (single !== undefined && single !== null && typeof single === 'object') {
    return [single];
  }
  return [];
}

export function normalizeVisitShipping(
  raw: unknown
): RouteMonitoringVisitShipping {
  const o = asRecord(raw) ?? {};
  return {
    number: pickNum(o, 'number', 'number'),
    commerceShippingNumber: pickStr(
      o,
      'commerceShippingNumber',
      'commerce_shipping_number'
    ),
    commercialShippingNumber: pickStr(
      o,
      'commercialShippingNumber',
      'commercial_shipping_number'
    ),
    recipient: pickStr(o, 'recipient', 'recipient'),
    visitType: pickStr(o, 'visitType', 'visit_type'),
    commerce: normalizeCommerceBlock(o.commerce),
    commercialService: normalizeCommercialServiceBlock(
      o.commercialService ?? o.commercial_service
    ),
    serviceName: pickStr(o, 'serviceName', 'service_name'),
    serviceCommercialName: pickStr(
      o,
      'serviceCommercialName',
      'service_commercial_name'
    ),
    serviceVisitTypeName: pickStr(
      o,
      'serviceVisitTypeName',
      'service_visit_type_name'
    ),
    serviceType: pickStr(o, 'serviceType', 'service_type'),
    logisticOperator: normalizeLogisticOperatorBlock(
      o.logisticOperator ?? o.logistic_operator
    ),
    serviceWithOperator: normalizeServiceWithOperatorBlock(
      o.serviceWithOperator ?? o.service_with_operator
    ),
  };
}
